import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { UserRound, CheckCircle2, Plus, Loader2, Upload, ImageIcon, XCircle, Mic, AlertTriangle, Cpu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getAvatars, getAvatar, createAvatar, getVoices, setAvatarDefaultVoice } from "@/lib/api";
import { useHeygenCreation } from "@/lib/useHeygenCreation";
import { HEYGEN_DISABLED_NOTICE } from "@/lib/providerFeatures";
import { useAuth } from "@/context/AuthContext";
import {
  createSubmitGuard, intentSignature, legacyReceiptKey, prepareLegacyReceipt, clearLegacyReceipt,
  classifySubmitError, AVATAR_DEFINITIVE_DETAILS,
} from "@/lib/legacyRequest";

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png"];

export default function MyAvatar() {
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const heygen = useHeygenCreation();
  const { user } = useAuth();
  const submitGuard = useRef(createSubmitGuard());

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Voice modal
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceAvatar, setVoiceAvatar] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState("");
  const [savingVoice, setSavingVoice] = useState(false);

  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await getAvatars();
      setAvatars(d.avatars || []);
    } catch {
      toast.error("Gagal memuat avatar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll any member avatars that are still processing
  useEffect(() => {
    const processing = avatars.filter((a) => !a.is_system && a.status === "processing");
    if (processing.length === 0) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const current = await getAvatars();
        const list = current.avatars || [];
        const stillProcessing = list.filter((a) => !a.is_system && a.status === "processing");
        // trigger backend poll for each processing avatar
        await Promise.all(stillProcessing.map((a) => getAvatar(a.id).catch(() => null)));
        const refreshed = await getAvatars();
        setAvatars(refreshed.avatars || []);
      } catch {
        // transient
      }
    }, 4000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [avatars]);

  const systemAvatars = avatars.filter((a) => a.is_system);
  const userAvatars = avatars.filter((a) => !a.is_system);

  const resetCreate = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setName("");
    setFile(null);
    setPreviewUrl(null);
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      toast.error("Format foto tidak didukung. Gunakan JPG, JPEG, atau PNG.");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Ukuran foto maksimal 10 MB.");
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const handleCreate = async () => {
    if (!heygen.enabled) {
      toast.error(HEYGEN_DISABLED_NOTICE);
      return;
    }
    if (name.trim() === "") {
      toast.error("Nama avatar wajib diisi.");
      return;
    }
    if (!file) {
      toast.error("Silakan upload foto presenter terlebih dahulu.");
      return;
    }
    if (submitting) return;
    // Synchronous claim: `submitting` is stale until the next render, so a fast double click
    // would otherwise send two different request ids.
    if (!submitGuard.current.tryClaim()) return;
    const receiptKey = legacyReceiptKey("avatar", user?.id);
    let receipt;
    try {
      // Stored BEFORE sending; the same name + photo always reuse the same request id.
      receipt = prepareLegacyReceipt(window.sessionStorage, receiptKey, intentSignature({
        name: name.trim(), file: [file.name, file.size, file.lastModified],
      }));
    } catch (e) {
      toast.error(e.message);
      submitGuard.current.release();
      return;
    }
    setSubmitting(true);
    try {
      await createAvatar(name.trim(), file, receipt.request_id);
      clearLegacyReceipt(window.sessionStorage, receiptKey);
      toast.success("Avatar sedang diproses...");
      setCreateOpen(false);
      resetCreate();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (classifySubmitError(e, AVATAR_DEFINITIVE_DETAILS) === "definitive") {
        clearLegacyReceipt(window.sessionStorage, receiptKey);
      }
      // Otherwise the avatar may exist: the receipt is kept, so retrying the same photo replays
      // the stored claim instead of creating another avatar.
      toast.error(typeof detail === "string" ? detail : "Gagal membuat avatar. Silakan coba lagi.");
    } finally {
      setSubmitting(false);
      submitGuard.current.release();
    }
  };

  const openVoiceModal = async (avatar) => {
    setVoiceAvatar(avatar);
    setSelectedVoice(avatar.default_voice_id || "");
    setVoiceOpen(true);
    if (voices.length === 0) {
      setVoicesLoading(true);
      try {
        const d = await getVoices();
        setVoices(d.voices || []);
      } catch {
        toast.error("Gagal mengambil daftar voice.");
      } finally {
        setVoicesLoading(false);
      }
    }
  };

  const handleSaveVoice = async () => {
    if (!voiceAvatar || selectedVoice === "") {
      toast.error("Voice belum dipilih.");
      return;
    }
    setSavingVoice(true);
    try {
      const voiceObj = voices.find((v) => v.voice_id === selectedVoice);
      await setAvatarDefaultVoice(voiceAvatar.id, {
        voice_id: selectedVoice,
        voice_name: voiceObj?.name || null,
      });
      toast.success("Voice berhasil disimpan.");
      setVoiceOpen(false);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Gagal menyimpan voice. Silakan coba lagi.");
    } finally {
      setSavingVoice(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Avatar</p>
          <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
            Avatar Saya
          </h1>
          <p className="mt-2 text-slate-500 md:text-lg">Gunakan avatar untuk membuat video Anda.</p>
        </div>
        <Button
          data-testid="open-create-avatar-btn"
          disabled={!heygen.enabled}
          onClick={() => setCreateOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white active:scale-95 transition-transform"
        >
          <Plus className="h-4 w-4 mr-2" /> Buat Avatar
        </Button>
      </div>

      {heygen.loaded && !heygen.enabled && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" role="status" data-testid="heygen-disabled-notice">
          {HEYGEN_DISABLED_NOTICE} Avatar yang sudah ada tetap dapat dilihat.
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Memuat...
        </div>
      ) : (
        <>
          {/* System avatars */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="system-avatars">
            {systemAvatars.map((a) => (
              <div
                key={a.id}
                data-testid={`avatar-card-${a.id}`}
                className="rounded-xl border border-slate-200 bg-white p-6 flex flex-col items-center text-center"
              >
                <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 overflow-hidden">
                  {a.thumbnail_url ? (
                    <img src={a.thumbnail_url} alt={a.name} className="h-full w-full object-cover" />
                  ) : (
                    <UserRound className="h-10 w-10" />
                  )}
                </div>
                <h3 className="mt-4 font-heading font-semibold text-slate-900">{a.name}</h3>
                <p className="text-sm text-slate-500">Avatar Default</p>
                <span className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Aktif
                </span>
              </div>
            ))}
          </div>

          {/* Member avatars */}
          <div className="space-y-4">
            <h2 className="font-heading text-lg font-semibold text-slate-900">Avatar Pribadi</h2>
            {userAvatars.length === 0 ? (
              <div
                data-testid="user-avatars-empty"
                className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center"
              >
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                  <UserRound className="h-7 w-7 text-slate-400" />
                </div>
                <p className="mt-4 font-heading text-lg font-semibold text-slate-900">Belum ada Avatar.</p>
                <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
                  Upload foto Anda untuk membuat Avatar yang dapat digunakan untuk video jualan.
                </p>
                <Button
                  data-testid="add-avatar-btn"
                  disabled={!heygen.enabled}
                  onClick={() => setCreateOpen(true)}
                  variant="outline"
                  className="mt-4 active:scale-95 transition-transform"
                >
                  <Plus className="h-4 w-4 mr-2" /> Buat Avatar
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="member-avatars">
                {userAvatars.map((a) => (
                  <MemberAvatarCard key={a.id} avatar={a} onChangeVoice={() => openVoiceModal(a)} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Create Avatar Modal */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) resetCreate(); }}>
        <DialogContent className="max-w-md" data-testid="create-avatar-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Buat Avatar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="avatar-name" className="text-slate-700">Nama Avatar</Label>
              <Input
                id="avatar-name"
                data-testid="avatar-name-input"
                placeholder="Contoh: Avatar Budi"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Foto Presenter</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="avatar-photo-input"
              />
              {!file ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="avatar-photo-dropzone"
                  className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center transition-colors hover:border-blue-400 hover:bg-blue-50/40"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                    <Upload className="h-5 w-5" />
                  </span>
                  <span className="font-medium text-slate-800">Upload Foto Presenter</span>
                  <span className="text-xs text-slate-500">JPG, JPEG, atau PNG • maksimal 10 MB</span>
                </button>
              ) : (
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                  <img src={previewUrl} alt="Preview" data-testid="avatar-preview" className="h-20 w-20 rounded-lg object-cover border border-slate-200" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{file.name}</p>
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="mt-2">
                      <ImageIcon className="h-4 w-4 mr-2" /> Ganti Foto
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="submit-create-avatar-btn"
              onClick={handleCreate}
              disabled={submitting || !heygen.enabled}
              className="bg-blue-600 hover:bg-blue-700 text-white active:scale-95 transition-transform"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              {submitting ? "Memproses..." : "Buat Avatar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voice selection modal */}
      <Dialog open={voiceOpen} onOpenChange={setVoiceOpen}>
        <DialogContent className="max-w-md" data-testid="voice-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Pilih Voice</DialogTitle>
          </DialogHeader>
          {voicesLoading ? (
            <div className="flex items-center gap-2 text-slate-500 py-8 justify-center">
              <Loader2 className="h-5 w-5 animate-spin" /> Memuat voice...
            </div>
          ) : (
            <div className="space-y-3">
              <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                <SelectTrigger data-testid="voice-select">
                  <SelectValue placeholder="Pilih voice" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {voices.map((v) => (
                    <SelectItem key={v.voice_id} value={v.voice_id} data-testid={`voice-option-${v.voice_id}`}>
                      {v.name} {v.language ? `— ${v.language}` : ""} {v.gender ? `(${v.gender})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">Voice diambil dari HeyGen Voice Library.</p>
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="save-voice-btn"
              onClick={handleSaveVoice}
              disabled={savingVoice || voicesLoading || selectedVoice === ""}
              className="bg-blue-600 hover:bg-blue-700 text-white active:scale-95 transition-transform"
            >
              {savingVoice ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mic className="h-4 w-4 mr-2" />}
              {savingVoice ? "Menyimpan..." : "Simpan Voice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MemberAvatarCard = ({ avatar, onChangeVoice }) => {
  const a = avatar;
  return (
    <div
      data-testid={`member-avatar-card-${a.id}`}
      className="rounded-xl border border-slate-200 bg-white p-6 flex flex-col items-center text-center"
    >
      <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 overflow-hidden">
        {a.thumbnail_url ? (
          <img src={a.thumbnail_url} alt={a.name} className="h-full w-full object-cover" />
        ) : (
          <UserRound className="h-10 w-10" />
        )}
      </div>
      <h3 className="mt-4 font-heading font-semibold text-slate-900">{a.name}</h3>

      {a.status === "processing" && (
        <span
          data-testid={`avatar-status-processing-${a.id}`}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sedang diproses...
        </span>
      )}

      {a.status === "failed" && (
        <span
          data-testid={`avatar-status-failed-${a.id}`}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-700"
        >
          <XCircle className="h-3.5 w-3.5" /> Gagal dibuat
        </span>
      )}

      {a.status === "ready" && (
        <>
          <span
            data-testid={`avatar-status-ready-${a.id}`}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700"
          >
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Siap
          </span>

          {/* Avatar engine */}
          {a.avatar_iii_supported ? (
            <span
              data-testid={`avatar-iii-yes-${a.id}`}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700"
            >
              <Cpu className="h-3.5 w-3.5" /> Avatar III tersedia
            </span>
          ) : (
            <span
              data-testid={`avatar-iii-no-${a.id}`}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-amber-600"
            >
              <AlertTriangle className="h-3.5 w-3.5" /> Avatar III tidak tersedia untuk avatar ini
            </span>
          )}

          {/* Voice */}
          <div className="mt-4 w-full rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <Mic className="h-4 w-4 text-blue-600" />
              <span className="truncate" data-testid={`avatar-voice-name-${a.id}`}>
                {a.default_voice_name || (a.default_voice_id ? "Voice tersimpan" : "Belum ada voice")}
              </span>
            </div>
          </div>
          <Button
            data-testid={`change-voice-btn-${a.id}`}
            variant="outline"
            size="sm"
            onClick={onChangeVoice}
            className="mt-3 w-full active:scale-95 transition-transform"
          >
            <Mic className="h-4 w-4 mr-2" /> {a.default_voice_id ? "Ubah Voice" : "Pilih Voice"}
          </Button>
        </>
      )}
    </div>
  );
};
